// client/src/components/MapComponent.js

import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// ✅ STEP 1: Import the marker images directly from the leaflet package
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// ✅ STEP 2: Re-configure Leaflet's default icon paths
// This tells Leaflet where to find the images we just imported.
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

const defaultCenter = [30.2672, -97.7431]; // Austin, TX
const defaultZoom = 11;

const MapComponent = ({ filteredCourses }) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const courseMarkersLayerRef = useRef(null);
  
// eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Only run if we have a container div and the map hasn't been created yet
    if (mapContainerRef.current && !mapInstanceRef.current) {
      // 1. Create the map instance with the default view
      mapInstanceRef.current = L.map(mapContainerRef.current).setView(defaultCenter, defaultZoom);

      // 2. Add the tile layer (the map background)
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(mapInstanceRef.current);

      // ✨ NEW: Initialize the layer for course markers and add it to the map
      courseMarkersLayerRef.current = L.layerGroup().addTo(mapInstanceRef.current);

      // 3. Try to get the user's location
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLatLng = [position.coords.latitude, position.coords.longitude];
          // If we found the user, add a marker for them
          L.marker(userLatLng)
            .addTo(mapInstanceRef.current)
            .bindPopup("Your Approximate Location");

          // Optional: If there are no search results yet, center the map on the user
          if (!filteredCourses || filteredCourses.length === 0) {
            mapInstanceRef.current.setView(userLatLng, 13);
          }
        },
        (error) => {
          // If geolocation fails, the map simply remains on the default Austin view
          console.error("Geolocation error or permission denied:", error);
        }
      );
    }
  }, []); // ✅ Empty dependency array ensures this runs only once on mount

  // --- EFFECT 2: Update Course Markers and Bounds (Runs when `filteredCourses` changes) ---
  useEffect(() => {
    // Don't do anything until the map and the marker layer are ready
    if (!mapInstanceRef.current || !courseMarkersLayerRef.current) {
      return;
    }

    // 1. Clear only the course markers from the previous render
    courseMarkersLayerRef.current.clearLayers();

    // 2. If we have new courses, add them to the marker layer
    if (filteredCourses && filteredCourses.length > 0) {
      filteredCourses.forEach(course => {
        // Ensure course has valid coordinates before creating a marker
        if (course.latitude && course.longitude) {
          const courseLatLng = [parseFloat(course.latitude), parseFloat(course.longitude)];
          L.marker(courseLatLng)
            .addTo(courseMarkersLayerRef.current) // ✨ Add to the dedicated layer
            .bindPopup(`<strong>${course.name}</strong>`);
        }
      });

      // 3. Adjust the map view to fit all the new course markers
      const bounds = L.latLngBounds(filteredCourses.map(c => [c.latitude, c.longitude]));
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] }); // Add padding
    }
    
  }, [filteredCourses]); // ✅ This effect is now solely dependent on the search results

  return <div ref={mapContainerRef} style={{ height: '400px', width: '100%', borderRadius: '0.5rem' }} />;
};

export default MapComponent;